import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import ProviderForm from "./ProviderForm";
import {
    BadgeCheck,
    Phone,
    Mail,
    MapPin,
    Edit3,
    Send,
    Loader2,
    ArrowLeft,
    AlertCircle,
} from "lucide-react";

const UPDATE_FIELDS = [
    { key: "phone", label: "Phone Number" },
    { key: "email", label: "Email Address" },
    { key: "name", label: "Provider Name" },
    { key: "location", label: "Service Area / Location" },
    { key: "other", label: "Other" },
];

const ProviderDetailsSheet = ({ open, onOpenChange, provider, onUpdated }) => {
    const { user } = useAuth();
    const [mode, setMode] = useState("view"); // "view" | "edit" | "request-update"
    const [selectedFields, setSelectedFields] = useState([]);
    const [notes, setNotes] = useState("");
    const [sending, setSending] = useState(false);

    if (!provider) return null;

    const isOwnUnverified = provider.added_by_user_id === user?.id && !provider.is_verified;

    const handleFieldToggle = (key) => {
        setSelectedFields(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleRequestUpdate = async () => {
        if (selectedFields.length === 0) {
            toast.error("Please select at least one field to update.");
            return;
        }

        setSending(true);
        try {
            // Fetch the user's profile for contact info
            const { data: profile } = await supabase
                .from("profiles")
                .select("full_name")
                .eq("id", user.id)
                .single();

            const { data: authUser } = await supabase.auth.getUser();
            const userEmail = authUser?.user?.email || "Unknown";

            const response = await supabase.functions.invoke("send-service-emails", {
                body: {
                    action: "request-provider-update",
                    providerName: provider.name,
                    providerId: provider.id,
                    requestedFields: selectedFields.map(key => UPDATE_FIELDS.find(f => f.key === key)?.label || key),
                    notes,
                    userName: profile?.full_name || "HomeEzi User",
                    userEmail,
                    userId: user.id,
                },
            });

            if (response.error) throw response.error;

            toast.success("Update request sent to HomeEzi support. We'll review it shortly.");
            setMode("view");
            setSelectedFields([]);
            setNotes("");
        } catch (err) {
            console.error("Request update error:", err);
            toast.error("Failed to send update request. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const handleEditSuccess = (data) => {
        toast.success("Provider updated!");
        setMode("view");
        onUpdated?.();
    };

    const handleClose = (openState) => {
        if (!openState) {
            setMode("view");
            setSelectedFields([]);
            setNotes("");
        }
        onOpenChange(openState);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[85vh] rounded-t-3xl flex flex-col p-0"
                style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-8">
                    <div className="flex items-center gap-3">
                        {mode !== "view" && (
                            <button
                                onClick={() => setMode("view")}
                                className="p-1 rounded-lg hover:bg-muted transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        )}
                        <SheetTitle className="text-xl font-bold text-foreground">
                            {mode === "edit" ? "Edit Provider" : mode === "request-update" ? "Request Update" : provider.name}
                        </SheetTitle>
                    </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pb-4 px-6">
                    {mode === "view" && (
                        <div className="space-y-6">
                            {/* Provider Info Card */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-bold text-foreground">{provider.name}</h3>
                                    {provider.is_verified && (
                                        <BadgeCheck className="w-5 h-5 text-primary" />
                                    )}
                                    {!provider.is_verified && (
                                        <span className="text-[10px] bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                            Pending
                                        </span>
                                    )}
                                </div>

                                {provider.category && (
                                    <p className="text-sm text-muted-foreground font-medium">{provider.category}</p>
                                )}

                                {provider.phone && (
                                    <a href={`tel:${provider.phone}`} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors">
                                        <Phone className="w-4 h-4 text-primary shrink-0" />
                                        <span className="text-sm font-medium">{provider.phone}</span>
                                    </a>
                                )}

                                {provider.phone_2 && (
                                    <a href={`tel:${provider.phone_2}`} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors">
                                        <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <span className="text-sm font-medium">{provider.phone_2}</span>
                                    </a>
                                )}

                                {provider.email && (
                                    <a href={`mailto:${provider.email}`} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors">
                                        <Mail className="w-4 h-4 text-primary shrink-0" />
                                        <span className="text-sm font-medium truncate">{provider.email}</span>
                                    </a>
                                )}

                                {provider.service_area && (
                                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
                                        <MapPin className="w-4 h-4 text-primary shrink-0" />
                                        <span className="text-sm font-medium">{provider.service_area}</span>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3 pt-2">
                                {isOwnUnverified && (
                                    <Button
                                        className="w-full rounded-xl py-5 text-base font-bold gap-2"
                                        onClick={() => setMode("edit")}
                                    >
                                        <Edit3 className="w-4 h-4" />
                                        Edit Provider
                                    </Button>
                                )}

                                {provider.is_verified && (
                                    <Button
                                        variant="outline"
                                        className="w-full rounded-xl py-5 text-base font-bold gap-2"
                                        onClick={() => setMode("request-update")}
                                    >
                                        <AlertCircle className="w-4 h-4" />
                                        Request Update
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {mode === "edit" && (
                        <ProviderForm
                            initialData={provider}
                            onSuccess={handleEditSuccess}
                            onCancel={() => setMode("view")}
                        />
                    )}

                    {mode === "request-update" && (
                        <div className="space-y-6">
                            <p className="text-sm text-muted-foreground">
                                Select which details need updating for <strong>{provider.name}</strong>. Your request will be sent to HomeEzi support for review.
                            </p>

                            <div className="space-y-3">
                                <Label className="text-sm font-bold">What needs updating?</Label>
                                {UPDATE_FIELDS.map(({ key, label }) => (
                                    <label
                                        key={key}
                                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl cursor-pointer hover:bg-muted/50 transition-colors"
                                    >
                                        <Checkbox
                                            checked={selectedFields.includes(key)}
                                            onCheckedChange={() => handleFieldToggle(key)}
                                        />
                                        <span className="text-sm font-medium">{label}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="update-notes">Additional Notes (Optional)</Label>
                                <Textarea
                                    id="update-notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="e.g. Their new phone number is 021..."
                                    className="rounded-xl resize-none"
                                    rows={3}
                                />
                            </div>

                            <Button
                                className="w-full rounded-xl py-5 text-base font-bold gap-2"
                                onClick={handleRequestUpdate}
                                disabled={sending || selectedFields.length === 0}
                            >
                                {sending ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Submit Request
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default ProviderDetailsSheet;
